/**
 * Goal Card — individual savings goal display
 */

import React, { useState } from 'react';
import {
  Shield,
  Plane,
  Car,
  Home,
  Target,
  Pencil,
  Trash2,
  Plus,
} from 'lucide-react';
import { useRegion } from '../../contexts/RegionContext';
import {
  useDeleteSavingsGoal,
  useAddGoalProgress,
} from '../../hooks/useBudgetOverview';
import { countCalendarMonthsUntil } from '../../utils/budget-calculations';
import type { SavingsGoal, GoalCategoryType, GoalPriority } from '../../types/savings-goal.types';

interface GoalCardProps {
  goal: SavingsGoal;
  onEdit: (goal: SavingsGoal) => void;
}

const CATEGORY_ICONS: Record<GoalCategoryType, React.ElementType> = {
  safety_net: Shield,
  travel: Plane,
  transportation: Car,
  housing: Home,
  other: Target,
};

const CATEGORY_LABELS: Record<GoalCategoryType, string> = {
  safety_net: 'Safety Net',
  travel: 'Travel',
  transportation: 'Transportation',
  housing: 'Housing',
  other: 'Other',
};

function getPriorityColor(priority: GoalPriority): string {
  switch (priority) {
    case 'high':
      return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
    case 'medium':
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
    default:
      return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
  }
}

function getProgressColor(percent: number): string {
  if (percent >= 75) return 'bg-green-500';
  if (percent >= 50) return 'bg-blue-500';
  if (percent >= 25) return 'bg-yellow-500';
  return 'bg-red-500';
}

export function GoalCard({ goal, onEdit }: GoalCardProps): React.ReactElement {
  const { formatCurrency, formatDate } = useRegion();
  const deleteGoal = useDeleteSavingsGoal();
  const addProgress = useAddGoalProgress();
  const [showProgressForm, setShowProgressForm] = useState(false);
  const [progressAmount, setProgressAmount] = useState('');
  const [progressError, setProgressError] = useState<string | null>(null);

  const Icon = CATEGORY_ICONS[goal.categoryType] ?? Target;
  const remaining = goal.targetAmount - goal.currentAmount;
  const monthsRemaining = countCalendarMonthsUntil(goal.targetDate);

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${goal.name}"? This cannot be undone.`)) return;
    try {
      await deleteGoal.mutateAsync(goal.id);
    } catch (e) {
      console.error('Failed to delete goal:', e);
    }
  };

  const handleAddProgress = async (e: React.FormEvent) => {
    e.preventDefault();
    setProgressError(null);
    const amount = parseFloat(progressAmount);
    if (!amount || amount <= 0) {
      setProgressError('Enter a valid amount');
      return;
    }
    try {
      await addProgress.mutateAsync({
        goalId: goal.id,
        amount,
        contributionDate: new Date().toISOString().slice(0, 10),
      });
      setProgressAmount('');
      setShowProgressForm(false);
    } catch (err) {
      setProgressError(err instanceof Error ? err.message : 'Failed to add progress');
    }
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-5 bg-white dark:bg-gray-800">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center flex-shrink-0">
            <Icon className="h-5 w-5 text-purple-600 dark:text-purple-400" aria-hidden />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{goal.name}</h3>
            <div className="flex flex-wrap gap-2 mt-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {CATEGORY_LABELS[goal.categoryType]}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${getPriorityColor(goal.priority)}`}>
                {goal.priority}
              </span>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  goal.onTrackStatus === 'on_track'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                    : 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                }`}
              >
                {goal.onTrackStatus === 'on_track' ? 'On Track' : 'At Risk'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={() => onEdit(goal)}
            className="p-2 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label={`Edit ${goal.name}`}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteGoal.isPending}
            className="p-2 text-gray-500 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
            aria-label={`Delete ${goal.name}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mb-3">
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-gray-600 dark:text-gray-400">
            {formatCurrency(goal.currentAmount)} of {formatCurrency(goal.targetAmount)}
          </span>
          <span className="font-medium text-gray-900 dark:text-gray-100">{goal.percentComplete}%</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full transition-all ${getProgressColor(goal.percentComplete)}`}
            style={{ width: `${Math.min(100, goal.percentComplete)}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm mb-4">
        <div>
          <p className="text-gray-500 dark:text-gray-400 text-xs">Target Date</p>
          <p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(goal.targetDate)}</p>
        </div>
        <div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-xs">Months Remaining</p>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {monthsRemaining} mth
              </p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-xs">Days Remaining</p>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {goal.daysRemaining} day
              </p>
            </div>
          </div>
        </div>
        {goal.monthlyTarget != null && (
          <div>
            <p className="text-gray-500 dark:text-gray-400 text-xs">Monthly Target</p>
            <p className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(goal.monthlyTarget)}</p>
          </div>
        )}
        <div>
          <p className="text-gray-500 dark:text-gray-400 text-xs">Remaining</p>
          <p className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(remaining)}</p>
        </div>
      </div>

      {showProgressForm ? (
        <form onSubmit={handleAddProgress} className="flex gap-2 items-start">
          <div className="flex-1">
            <input
              type="number"
              min={0.01}
              step="0.01"
              value={progressAmount}
              onChange={(e) => setProgressAmount(e.target.value)}
              placeholder="Amount"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
              aria-label="Progress amount"
            />
            {progressError && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">{progressError}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={addProgress.isPending}
            className="px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => {
              setShowProgressForm(false);
              setProgressError(null);
            }}
            className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400"
          >
            Cancel
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setShowProgressForm(true)}
          className="flex items-center gap-2 text-sm font-medium text-green-600 dark:text-green-400 hover:text-green-700"
        >
          <Plus className="h-4 w-4" /> Add Progress
        </button>
      )}
    </div>
  );
}
